import type {
  CountryProfile,
  GlobalCompanyLocation,
  GlobalFacility,
  GlobalPowerRelationship,
  MapEvidenceSource,
} from '../types/global'

// Reviewed October 4, 2026. Coordinates are navigation anchors, not parcels.
// Source publication dates below are separate from the date of this review.
const checkedAt = '2026-10-04'
const evidence = (title: string, publisher: string, url: string): MapEvidenceSource => ({ title, publisher, url, checkedAt })

const nebiusFinland = evidence(
  'Nebius Finland expansion and Lille project update — March 31, 2026',
  'Nebius',
  'https://nebius.com/newsroom/nebius-to-construct-310-mw-ai-factory-in-finland',
)
const nebiusOperating = evidence(
  'Live GB300 NVL72 deployment in Finland — December 17, 2025',
  'Nebius',
  'https://nebius.com/blog/posts/first-live-nvidia-gb300-nvl72-deployment-in-europe',
)
const lappeenrantaConstruction = evidence(
  'Main construction began in July 2026 — August 11, 2026 contractor update',
  'Fira',
  'https://fira.fi/en/news/construction-and-project-development-company-fira-selected-as-main-contractor-for-one-of-europes-largest-ai-data-center-projects/',
)
const narvikConstruction = evidence(
  'Narvik construction and Nordscale operations partnership — July 6, 2026',
  'Nscale',
  'https://www.nscale.com/press-releases/nordscale',
)
const narvikMicrosoft = evidence(
  'Narvik Microsoft deployment planned for 2027 — April 14, 2026',
  'Nscale',
  'https://www.nscale.com/press-releases/nscale-microsoft-norway',
)
const nscaleFleet = evidence(
  'Fleet deployment update; Narvik commissioning not specified — October 1, 2026',
  'Nscale',
  'https://www.nscale.com/press-releases/execution-at-scale',
)
const anthropicTokyo = evidence(
  'Tokyo office opened — October 29, 2025',
  'Anthropic',
  'https://www.anthropic.com/news/opening-our-tokyo-office',
)
const anthropicSeoul = evidence(
  'Seoul office opened — June 17, 2026',
  'Anthropic',
  'https://www.anthropic.com/news/seoul-office-partnerships-korean-ai-ecosystem',
)

export const reviewedGlobalFacilities: GlobalFacility[] = [
  {
    slug: 'nebius-mantsala',
    name: 'Nebius Mäntsälä',
    countrySlug: 'finland',
    coordinates: [25.32, 60.63],
    coordinatePrecision: 'Mäntsälä municipality navigation anchor; not campus parcel',
    kind: 'Data center campus',
    status: 'Operating',
    verification: 'confirmed',
    // No unsupported company dossier links: these operators are not yet registered.
    operatorSlugs: [],
    description: 'Nebius reports an operating AI cloud campus and a completed expansion to 75 MW. This is reported site capacity, not measured electrical load. No dedicated generator-to-campus feed is established.',
    sourceIds: [],
    evidence: 'company-reported',
    evidenceSources: [nebiusFinland, nebiusOperating],
  },
  {
    slug: 'nebius-lappeenranta',
    name: 'Nebius Lappeenranta AI factory',
    countrySlug: 'finland',
    coordinates: [28.19, 61.06],
    coordinatePrecision: 'Lappeenranta city anchor; Pajarila area documented, parcel not geocoded',
    kind: 'Data center campus',
    status: 'Under construction',
    verification: 'under-construction',
    operatorSlugs: [],
    description: 'Contractor Fira confirms main construction began in July 2026 in the Pajarila industrial area. The first building is expected in the first half of 2027; 310 MW is the completed-campus target, not current energized capacity.',
    sourceIds: [],
    evidence: 'company-reported',
    evidenceSources: [lappeenrantaConstruction, nebiusFinland],
  },
  {
    slug: 'nscale-narvik',
    name: 'Nscale Narvik AI campus',
    countrySlug: 'norway',
    coordinates: [17.43, 68.44],
    coordinatePrecision: 'Narvik regional anchor; Kvandal/Bjerkvik campus is outside this city-center point',
    kind: 'Data center campus',
    status: 'Under construction',
    verification: 'under-construction',
    operatorSlugs: [],
    description: 'Nscale documents construction-phase services at its Kvandal campus near Narvik. The 230 MW campus and Microsoft GPU deployment include future milestones. Its October 2026 fleet update does not verify Narvik operations; no named direct hydropower feed is established.',
    sourceIds: [],
    evidence: 'company-reported',
    evidenceSources: [narvikConstruction, narvikMicrosoft, nscaleFleet],
  },
  {
    slug: 'nebius-lille-region',
    name: 'Nebius Lille-area AI factory',
    countrySlug: 'france',
    coordinates: [3.06, 50.63],
    coordinatePrecision: 'Lille-region navigation anchor; primary source says near Lille, parcel unverified',
    kind: 'Data center campus',
    status: 'Under construction',
    verification: 'under-construction',
    operatorSlugs: [],
    description: 'Nebius reported in March 2026 that it was building an AI factory near Lille, targeting 240 MW when fully deployed. The reviewed sources do not establish commissioning or the precise site. This is separate from the Paris research cluster.',
    sourceIds: [],
    evidence: 'company-reported',
    evidenceSources: [nebiusFinland],
  },
]

export const reviewedCompanyLocations: GlobalCompanyLocation[] = [
  {
    slug: 'anthropic-tokyo',
    name: 'Anthropic · Tokyo',
    countrySlug: 'japan',
    coordinates: [139.69, 35.68],
    coordinatePrecision: 'Tokyo city anchor; office street address not established by this source',
    locationStatus: 'Current office',
    description: 'Anthropic announced its Tokyo office opening in October 2025. This office-city pin does not identify an AI data center, model-serving location, or global headquarters.',
    officialUrl: anthropicTokyo.url,
    evidenceSources: [anthropicTokyo],
  },
  {
    slug: 'anthropic-seoul',
    name: 'Anthropic · Seoul',
    countrySlug: 'south-korea',
    coordinates: [126.98, 37.56],
    coordinatePrecision: 'Seoul city anchor; office street address not established by this source',
    locationStatus: 'Current office',
    description: 'Anthropic announced its Seoul office opening in June 2026. This office is distinct from NAVER Cloud in Seongnam and is not evidence of a model-serving data center.',
    officialUrl: anthropicSeoul.url,
    evidenceSources: [anthropicSeoul],
  },
]

// Neither construction partnerships nor clean-energy marketing establish a
// specific generator, utility service contract, or physical power route here.
export const reviewedGlobalPowerRelationships: GlobalPowerRelationship[] = []

// Both country slugs were absent from globalRegistry.ts when reviewed.
// Existing Europe/Asia region values suffice. Extra evidenceSources retain the
// primary support even though CountryProfile currently only requires sourceIds.
export const reviewedCountries: Array<CountryProfile & { evidenceSources: MapEvidenceSource[] }> = [
  {
    slug: 'norway', name: 'Norway', iso2: 'NO', numericId: '578', region: 'Europe',
    coordinates: [9, 62],
    summary: 'Nscale reports an AI campus under construction in the Narvik region, with future Microsoft deployment plans.',
    strategy: 'This profile records the sourced campus; it does not infer a national policy, operating load, or dedicated power supply.',
    companySlugs: [], sourceIds: ['nscale'], evidence: 'company-reported',
    evidenceSources: [narvikConstruction, narvikMicrosoft],
  },
  {
    slug: 'japan', name: 'Japan', iso2: 'JP', numericId: '392', region: 'Asia',
    coordinates: [138, 37],
    summary: 'Anthropic reports an open Tokyo office. This is company presence, not a mapped compute facility.',
    strategy: 'This profile records sourced office activity separately from national compute capacity or power infrastructure.',
    companySlugs: ['anthropic'], sourceIds: ['anthropic'], evidence: 'company-reported',
    evidenceSources: [anthropicTokyo],
  },
]
